/**
 * yearBoundaries (#2501) — pure "where does the year turn over" math behind the
 * vertical year-boundary marker shared by the three app time-series charts
 * (preisspiegel / liquiditaetsplanung / ernteprognose).
 *
 * On multi-year ranges (e.g. Preisspiegel *Max*, 2024→2026) the x-axis date ticks
 * repeat across years with no visual break, so it is hard to tell which points
 * belong to which year. A dotted vertical at each 01.01 inside the visible domain
 * — styled like the existing "Heute" line — gives that break as a background
 * guide.
 *
 * Two layers, both pure (no React, no recharts — recharts does not render under
 * jsdom, so this is where the behaviour is actually tested):
 *
 *   1. ``yearBoundariesInDomain(min, max)`` — the 01.01 boundaries strictly
 *      inside a date domain. "Strictly" excludes a boundary landing on the domain
 *      START (nothing earlier is on-plot to separate it from, and a line flush
 *      against the y-axis is noise); a boundary at the domain END is kept,
 *      because there the turnover itself is visible.
 *
 *   2. ``yearBoundaryMarkers(categories)`` — those boundaries SNAPPED onto real
 *      axis categories. This layer exists for the charts still on CATEGORICAL
 *      (string) x-axes (see ``./timeWindow``), where recharts only draws a
 *      ``<ReferenceLine x=…>`` whose value is an ACTUAL category on the axis — an
 *      off-axis date silently renders nothing. A boundary therefore anchors on
 *      the first visible category at-or-after 01.01: the first bucket of the new
 *      year, which is where the year visually turns over.
 *
 *      #2543 — Preisspiegel has since moved to a NUMERIC (epoch-ms) axis and uses
 *      ``yearBoundaryLinesAt`` instead, which needs no snapping: a time axis
 *      places the line at the real ``Date.UTC(year, 0, 1)`` whether or not an
 *      observation falls there. (``aggregateChartModel`` still emits a ``today``
 *      row, but now only as a vertex of the dashed forecast line — no longer to
 *      make "Heute" renderable.) Liquiditätsplanung and Ernteprognose remain
 *      categorical, so this layer stays.
 *
 * Bucket keys across the three charts are year-leading and lexicographically
 * sortable — ``YYYY`` / ``YYYY-MM`` / ``YYYY-MM-DD`` (see ``bucketKey`` in
 * ernteprognose's ``outputSeries``) — so the year is the leading four characters
 * and this module stays grain-agnostic.
 */

/** A 01.01 year boundary inside a chart's visible date domain. */
export interface YearBoundary {
  /** The year that STARTS at this boundary. */
  year: number;
  /** The boundary date itself, ISO ``YYYY-01-01``. */
  date: string;
  /** Axis label for the marker, e.g. ``"2025"``. */
  label: string;
}

/** A {@link YearBoundary} resolved to a category that exists on the axis. */
export interface YearBoundaryMarker {
  /** The axis category to anchor the ``<ReferenceLine>`` on. */
  x: string;
  /** The year that starts at (or first becomes visible on) this category. */
  year: number;
  /** Axis label for the marker, e.g. ``"2025"``. */
  label: string;
}

/** The leading-4-character year of a bucket key, or ``null`` if unparseable. */
function yearOf(key: string): number | null {
  if (!/^\d{4}/.test(key)) return null;
  return Number(key.slice(0, 4));
}

/**
 * The 01.01 boundaries strictly inside the inclusive date domain
 * ``[minKey, maxKey]`` — one per year the domain crosses, in ascending order.
 *
 * Expressed as year arithmetic rather than string comparison so it works for
 * every grain (``YYYY`` / ``YYYY-MM`` / ``YYYY-MM-DD``) without a
 * shorter-prefix-sorts-first pitfall. Every returned boundary is inside the
 * domain by construction: for ``y`` in ``yearOf(min)+1 … yearOf(max)``,
 * ``${y}-01-01`` is after ``minKey`` (a later year) and not after ``maxKey``
 * (whose year is ``≥ y``). A single-year domain yields nothing.
 */
export function yearBoundariesInDomain(minKey: string, maxKey: string): YearBoundary[] {
  const from = yearOf(minKey);
  const to = yearOf(maxKey);
  if (from == null || to == null) return [];

  const out: YearBoundary[] = [];
  for (let year = from + 1; year <= to; year += 1) {
    out.push({ year, date: `${year}-01-01`, label: String(year) });
  }
  return out;
}

/**
 * Year-boundary markers for a categorical date axis, given the ASCENDING list of
 * currently visible categories (the zoom window's rows — so panning/zooming a
 * boundary off-plot drops its marker for free).
 *
 * Each boundary anchors on the first visible category of its year. When a whole
 * year has no data, consecutive boundaries would resolve to the SAME category —
 * they collapse into one marker (labelled with that category's own year) so two
 * lines never stack invisibly at one x.
 */
export function yearBoundaryMarkers(categories: readonly string[]): YearBoundaryMarker[] {
  if (categories.length < 2) return [];

  const boundaries = yearBoundariesInDomain(categories[0], categories[categories.length - 1]);
  // Keyed by anchor so a shared anchor keeps the last (highest) year — i.e. the
  // anchor category's own year, which is what its label should read.
  const byAnchor = new Map<string, YearBoundaryMarker>();

  for (const boundary of boundaries) {
    const anchor = categories.find((c) => {
      const y = yearOf(c);
      return y != null && y >= boundary.year;
    });
    if (anchor === undefined) continue;
    const year = yearOf(anchor) ?? boundary.year;
    byAnchor.set(anchor, { x: anchor, year, label: String(year) });
  }

  return [...byAnchor.values()];
}
